<template>
  <section class="choose-coffee-section">
    <div class="row mx-0 mb-4 mb-lg-3">
      <div class="col-6 pr-2 col-sm-8 px-0">
        <h5 class="single-page-signup-title m-0">
          Choose your first coffee
        </h5>
        <p class="desktop-xs single-page-signup-subtitle">
          You'll get new varieties to try each month
        </p>
      </div>
      <div class="col-6 col-sm-4 px-0 d-flex align-items-start justify-content-end">
        <div class="single-page-signup-toggle mt-0">
          <span
            :class="{ 'single-page-signup-toggle-option': true, active: !userData.decaf }"
            @click="setDecaf(false)"
          >Regular</span>
          <span
            :class="{ 'single-page-signup-toggle-option': true, active: userData.decaf }"
            @click="setDecaf(true)"
          >Decaf</span>
        </div>
      </div>
      <div class="mobile-xs col-12 px-0 mt-2">
        <p class="single-page-signup-subtitle">
          You'll get new varieties to try each month
        </p>
      </div>
    </div>

    <div class="cards-container">
      <div class="row mx-0">
        <div class="col-xl-11 col-xxl-10 col-width px-0 mx-auto">
          <signup-coffee-range-element-loader v-if="isLoading || (coffees_to_show.length === 0 && userData.coffee_type !== undefined)" />
          <div
            v-else
            class="row mx-0"
          >
            <div
              v-for="(coffee, index) in coffees_to_show"
              :key="index"
              :class="{
                'col-12 mb-sm-3 mb-lg-2 px-xl-0': true,
                'mx-auto': coffees_to_show.length === 1,
              }"
            >
              <signup-coffee-range-element
                :product="coffee"
                :index="index"
                :is_picked="isPickedCoffee(coffee)"
                @pick-coffee="pickCoffeeBag"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { inject, ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import SignupCoffeeRangeElement from './SignupCoffeeRangeElement.vue'
import SignupCoffeeRangeElementLoader from './SignupCoffeeRangeElementLoader.vue'
import * as GTM from '../../../utils/analytics/gtm'

const store = useStore()
const userData = inject('userData')
const voucherData = inject('voucherData')
const isLoading = inject('isLoading')
const updateUserData = inject('updateUserData')
const setPlanCache = inject('setPlanCache')
// Reactive variables (inc composables)
const picked_coffee = ref(undefined)
const previous_type_name = ref('')
// Computed
const coffees_range = computed(() => {
  return store.getters['shop/coffees_range']
})

const coffees_to_show = computed(() => {
  return prepCoffeesToShow(coffees_range.value)
})
// Methods
function prepCoffeesToShow(coffees_range) {
  return coffees_range.map((coffee, index) => {
    return { ...coffee, index: index }
  })
}

function isPickedCoffee(coffee_to_compare) {
  return coffee_to_compare?.sku === picked_coffee.value?.sku
}

function getCoffeePlans(coffee) {
  var plans = {}
  coffee.coffee_plans.forEach((plan) => {
    plans = {
      ...plans,
      ...plan
    }
  })
  return plans
}

function type_name_changed(){
  return previous_type_name.value !== '' && previous_type_name.value !== userData.value.type_name
}

function setDecaf(isDecaf) {
  isLoading.value = true
  updateUserData({
    decaf: isDecaf,
    type_name: isDecaf ? 'decaf' : 'regular',
  })
  store.dispatch('shop/getFunnelCoffeesRange', {
    coffee_type: userData.value.decaf ? 'brewed' : userData.value.coffee_type,
    decaf: userData.value.decaf,
    voucher_code: voucherData.value.voucher_code
  }).finally(() => {
    setTimeout(() => isLoading.value = false, 1000)
  })
  GTM.funnelProgress(5, 'pick_type', { type: userData.value?.type_name }, 'bag')
  setPlanCache(3, type_name_changed() === true ? true : false)
}

function planCoffeeSku(plan, sku) {
  if (!sku) return undefined
  var variant_suffix = plan.amount === 1000 ? '-G1000' : '-G250'
  var coffee_sku = sku.split('-')[0] + variant_suffix
  return coffee_sku
}

function pickCoffeeBag(coffee) {
  if(picked_coffee.value !== undefined && picked_coffee.value.sku === coffee.sku) {
    picked_coffee.value = undefined
    updateUserData({
      plan: {
        name: undefined,
        price: 0,
        pioneer_price: 0,
        sku: undefined,
        coffee_type: undefined,
        discount_price: undefined,
        discount_pioneer_price: undefined
      },
      coffee: {
        name: undefined,
        sku: undefined,
        primary_image: undefined,
        card_thumbnail: undefined,
        label_artwork: undefined,
      },
      coffee_card_thumbnail_250g: undefined,
      coffee_card_thumbnail_1kg: undefined,
      coffee_label_artwork: undefined,
      total: 0,
      subtotal: 0,
      discount: 0,
      voucher_discount: 0,
      balance_discount: 0,
      amount: 0
    })
    return
  }
  picked_coffee.value = coffee
  var amount = userData.value.amount === 0 ? '250g' : userData.value.amount === 1000 ? '1Kg' : userData.value.amount+'g'
  var plan = getCoffeePlans(picked_coffee.value)[amount]
  const plan_price = plan.discounted_price !== null ? plan.discounted_price : plan.price
  const plan_pioneer_price = plan.pioneer_discounted_price !== null ? plan.pioneer_discounted_price : plan.pioneer_price
  var coffee_card_thumbnail = userData.value.amount === 0 ?
    picked_coffee.value.card_thumbnail : userData.value.amount === 1000 ? picked_coffee.value.card_thumbnail_1kg : picked_coffee.value.card_thumbnail
  updateUserData({
    plan: {
      name: plan.plan.includes('_plan') ? plan.plan : plan.plan + '_plan',
      sku: plan.coffee_plan_sku,
      price: plan.price,
      pioneer_price: plan.pioneer_price,
      coffee_type: userData.value.coffee_type,
      discounted_price: plan.discounted_price,
      pioneer_discounted_price: plan.pioneer_discounted_price
    },
    coffee: {
      name: picked_coffee.value.name,
      sku: userData.value.amount !== 0 ? planCoffeeSku(plan, picked_coffee.value.sku) : picked_coffee.value.sku,
      card_thumbnail: coffee_card_thumbnail,
      ribbon: picked_coffee.value?.ribbon,
      label_artwork: picked_coffee.value.label_artwork,
    },
    coffee_card_thumbnail_250g: picked_coffee.value.card_thumbnail,
    coffee_card_thumbnail_500g: picked_coffee.value.card_thumbnail_500g,
    coffee_card_thumbnail_1kg: picked_coffee.value.card_thumbnail_1kg,
    coffee_label_artwork: picked_coffee.value.label_artwork,
    total: userData.value.is_pioneer ? plan_pioneer_price : plan_price,
    subtotal: plan.price,
    discount: userData.value.is_pioneer ? plan.pioneer_price_discount : plan.price_discount,
    decaf: userData.value.decaf,
    amount: userData.value.amount !== 0 ? userData.value.amount : 0,
  })
  GTM.funnelProgress(6, 'pick_range', {
    range: plan.plan,
    amount: 0,
    range_sku: plan.coffee_plan_sku,
    coffee: picked_coffee.value.name,
    coffee_sku: userData.value.amount !== 0 ? planCoffeeSku(plan, picked_coffee.value.sku) : picked_coffee.value.sku,
  }, 'bag')

  setPlanCache(4, true)
}

function getCoffeeBySku(sku) {
  var coffee = coffees_to_show.value.find((coffee) => {
    return coffee.sku === sku
  })
  var picked_coffee_by_sku = undefined
  if (coffee !== undefined)
    picked_coffee_by_sku = {
      ...coffee,
      amount: parseInt(userData.value?.coffee.sku.split('G')[1])
    }
  return picked_coffee_by_sku
}

watch(coffees_to_show, (newV) => {
  if (newV && newV.length > 0) {
    isLoading.value = false
  }
  if (newV) {
    if (userData.value?.coffee?.sku && userData.value?.plan?.sku && userData.value?.coffee_type === userData.value?.plan?.coffee_type) {
      picked_coffee.value = getCoffeeBySku(userData.value?.coffee.sku.split('-')[0])
        ? getCoffeeBySku(userData.value?.coffee.sku.split('-')[0])
        : undefined
    }
  }
})
// Lifecycle Hooks
onMounted(() => {
  setTimeout(() => {
    if (userData.value?.coffee?.sku && userData.value?.plan?.sku && userData.value?.coffee_type === userData.value?.plan?.coffee_type) {
      picked_coffee.value = getCoffeeBySku(userData.value?.coffee.sku.split('-')[0])
        ? getCoffeeBySku(userData.value?.coffee.sku.split('-')[0])
        : undefined
    }
  }, 150)
})
</script>

<script>
export default {
  name: 'ChooseYourCoffee',
}
</script>

<style lang="scss" scoped>
.choose-coffee-section {
  padding-top: 30px;
  padding-bottom: 30px;
  .col-width {
    @media (min-width: 1200px) {
      max-width: 860px;
    }
  }
}
</style>
